// src/context/CompanyContext.jsx
import { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import companyService from '../services/company.service';

const CompanyContext = createContext();

export const useCompany = () => {
  const context = useContext(CompanyContext);
  if (!context) {
    throw new Error('useCompany must be used within CompanyContextProvider');
  }
  return context;
};

export const CompanyContextProvider = ({ children }) => {
  const { user, isSuperAdmin } = useAuth();
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);

  // Load saved company from localStorage
  useEffect(() => {
    if (isSuperAdmin) {
      const savedCompanyId = localStorage.getItem('selectedCompanyId');
      if (savedCompanyId) {
        const companyId = parseInt(savedCompanyId);
        // Set selected company
        setSelectedCompany({ id: companyId });
        // Fetch full company details
        fetchCompanyDetails(companyId);
      }
      // Fetch all companies for dropdown
      fetchCompanies();
    } else if (user?.companyId) {
      // Regular admin - use their assigned company
      setSelectedCompany({ id: user.companyId, name: user.companyName });
    }
  }, [user, isSuperAdmin]);

  const fetchCompanies = async () => {
    try {
      const response = await companyService.getAllCompanies();
      if (response.success) {
        setCompanies(response.data);
      }
    } catch (error) {
      console.error('Error fetching companies:', error);
    }
  };

 const fetchCompanyDetails = async (companyId) => {
  try {
    const response = await companyService.getCompanyById(companyId);
    const data = response.data?.data || response.data || response;
    const name = data?.name || data?.companyName || data?.company?.name;
    
    if (name) {
      setSelectedCompany(prev => ({ ...prev, id: companyId, name }));
    }
  } catch (error) {
    console.error('Error fetching company details:', error);
  }
};

  const selectCompany = (company) => {
    setSelectedCompany(company);
    localStorage.setItem('selectedCompanyId', company.id.toString());
    window.location.reload();
  };

  const clearCompany = () => {
    setSelectedCompany(null);
    localStorage.removeItem('selectedCompanyId');
  };

  // Get companyId for API calls
  const getCompanyId = () => {
    if (isSuperAdmin) {
      return selectedCompany?.id || null;
    }
    return user?.companyId || null;
  };

  const value = {
    selectedCompany,
    companies,
    selectCompany,
    clearCompany,
    getCompanyId,
    isSuperAdmin,
    loading,
  };

  return (
    <CompanyContext.Provider value={value}>
      {children}
    </CompanyContext.Provider>
  );
};